/***
 *
 * @file shared/RenderHtml/index.tsx
 * @description This will be used for creating html structure.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 13 Feb 2024
 *
 */


// import Block from "@/components/Block";
import Box from "@/components/Box";
import Button from "@/components/Button";
import Card from "@/components/Card";
import CustomLink from "@/components/CustomLink";
import DynamicFormWrapper from "@/components/DynamicForm/Wrapper";
import InputField from "@/components/InputField";
import Overlay from "@/components/Overlay";
import Select from "@/components/Select";
import { deepCopy } from "@/helpers/utils.helper";
import classNames from "classnames";
// import { theme } from "@/themes/variables.theme";
// import Link from "next/link";
import React, { lazy } from "react";
import { StyledDate } from "../StyledComponents";
import IframeWrapper from "@/components/IframeWrapper";
import { Document } from "@contentful/rich-text-types";


import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import DomParser from "@/components/DomParser/DomParser";
// import { useContentfulInspectorMode, useContentfulLiveUpdates } from "@contentful/live-preview/react";
import { T_ContentItem, T_RLData } from "@/types/contentful.type";
import { getDefaultLocale } from "@/config/app.config";



const Media = lazy(() => import("@/components/Media"));
const Typography = lazy(() => import("@/components/Typography"));
const RichTextBox = lazy(() => import("@/components/RichTextBox"));
const Section = lazy(() => import("@/layouts/Section"));
const Row = lazy(() => import("@/layouts/Row"));
const Column = lazy(() => import("@/layouts/Column"));

/**
 * to specify prop types for RenderHtml function
 */
interface I_Props {
  /** contains the content type */
  cType?: string,

  /** contains the data served by Contentful */
  data?: T_RLData,

  /** index with respect to RenderHtml function */
  rendererIndex: number,

  /** contains the JSON structure as provided by Contentful */
  struct?: T_ContentItem,

  /** contains the locale as provided by Contentful */
  locale?: string,

  /** contains the slug as provided by Contentful */
  slug?: string,

  localeList?: string[]
}

/**
 * to specify prop types for HtmlView function
 */
type T_ViewProps = Omit<I_Props, 'rendererIndex'> & {
  /** index of the highest level element rendered by HtmlView function */
  viewIndex?: number,
  id?: string | undefined,
  className?: string,
  numberOfColumns?: number
}

const generateKey = () => {
  return Math.floor(Math.random() * 10000000);
}

/**
 * Name: AddRow
 * 
 * Description: Add a row layout in the UI. It handles the following cases:
 * 
 * - if the data it receives (rlData), contains a list of columns, it invokes AddColumn function
 * 
 * - if the data contains the contentList property, which is an array, it cycles through every element to reder its respective content,
 *   which could be an entire column or may contain column elements like text and media
 * 
 * @param param0  object containing the following properties:
 *                - rlData: an object containing the content to be displayed inside the row
 *                - clKey: key of an element inside a list for unique identification
 *  
 * @returns html data containing the contents of the added row
 */
export const AddRow = ({ id = "", parentId = "row", rlData={}, className = "" }: { id?: string | undefined, parentId?: string, rlData?: T_RLData, className?: string }) => {
  let rowElementId = `${parentId && parentId != "row" ? parentId + "-" : ""}row`;
  return (
    <>
      {/* <pre>{JSON.stringify(rlData, null, 2)}</pre> */}
      {rlData?.hasOwnProperty("columns") ? (
        <>
          <Row id={`sect-${rlData?.id}-row`} className={`${className || ''}`}>
            {/* <pre>{JSON.stringify(rlData, null, 2)}</pre> */}
            <AddColumn id={id} className={className} parentId={rowElementId} clContent={rlData} />
            {/* <HtmlView cType={data?.contentType} viewIndex={dIndex} data={data} /> */}
          </Row>
        </>
      ) : (
        rlData?.hasOwnProperty('contentList') ? (
          rlData?.contentList?.map((ct: T_ContentItem) => {
            return (
              <AddContent id={id} className={className} key={ct?.id + "---" + generateKey()} ctType={ct?.contentType} parentId={ct?.id} ctData={ct} />
            )
          })
        )
          :
          (
            (rlData?.content && Array.isArray(rlData?.content?.contentList)) ?? rlData?.content?.contentList?.map((el: T_ContentItem, ii: number) => {
              rowElementId += `-${el?.id}}`;
              if ((el?.content?.hasOwnProperty("contentList") || el?.content?.hasOwnProperty("columns"))) {
                return (
                  <AddRow id={id} key={`rowElementId-${generateKey()}`} parentId={`${rowElementId}`} rlData={el?.content} className={classNames(el?.title, className)} />
                )
              } else {
                return (
                  <PopulateColumnData id={id} className={className} key={`rowElementId-${generateKey()}`} dId={el?.id} dType={el?.contentType} dIndex={ii} clData={el} />
                )
              }
            })
          )
      )}
    </>
  )
}

/**
 * Name: AddColumn
 * 
 * Description: Function to render a column with content served by contentful. It is handled in the following ways:
 *               
 * - if there is a list of columns in an array, iterate through each one and add content to it
 *               
 * - else if current column contains a list of columns (section), add a row
 *
 * - else for a column with non-column contents, add content to it
 * @param param0 object containing the following properties:
 *                - clContent: an object containing the content to be displayed inside the column
 *                - props: props passed from the parent
 * @returns html content containing the contents of an added column
 */
export const AddColumn = ({ id = "", className = "", clContent, parentId = "clm" }: { id?: string | undefined, className?: string, clContent: T_RLData, parentId: string, props?: undefined }) => {
  let columnElementId = `${parentId && parentId != "clm" ? parentId + "-" : ""}clm`
  return (
    Array.isArray(clContent?.columns) ? (
      clContent?.columns && clContent?.columns?.map((clData: T_ContentItem) => {
        columnElementId += `-${clData?.id}`
        return (
          <HtmlView id={id || columnElementId} key={`columnElementId-${generateKey()}`} className={className} numberOfColumns={clContent?.columns?.length || 1} cType={clData?.contentType} data={clData} />
        )
      })
    ) : (
      <>
        <HtmlView id={id || columnElementId} key={`columnElementId-${generateKey()}`} className={className} numberOfColumns={clContent?.content?.columns?.length || 1} cType={clContent?.contentType} data={clContent} />
      </>

    )
  )
}

/**
 * Name: AddContent
 * 
 * Description: function to add HTML content within the layout in the UI using data received from Contentful
 * 
 * - recursively call AddContent itself if content is a list of columns, not leaf component
 * 
 * - if leaf component is received, call PopulateColumnData to populate the content within a Column layout
 * 
 * @param param0 object containing the following properties:
 *                - ctData: an object containing the content to be displayed in the UI
 *                - ctType: string representing the content type
 * 
 * @returns html content to be displayed on the UI
 */
const AddContent = ({ id = "", className = "", parentId = "cnt", ctData }: { id?: string | undefined, className?: string, ctType: string | undefined, parentId: string | undefined, ctData: T_RLData }) => {
  let contentElementId = `${parentId && parentId != "cnt" ? parentId + "-" : ""}cnt`
  return (
    (ctData?.content?.hasOwnProperty("contentList") && ctData?.content?.contentList?.[0]?.content?.hasOwnProperty("contentList")) ? (
      ctData?.content?.contentList?.map((cl: T_ContentItem, ind: number) => {
        contentElementId += `-${cl?.id}`;
        if (cl?.content?.useAs && ["Row", "Box", "Card", "Block"].includes(cl?.content?.useAs as string)) {
          return (
            <HtmlView id={id || contentElementId} className={className} key={`contentElementId-${generateKey()}-${ind}`} cType={cl?.contentType} data={cl} />
          )
        } else {
          return (
            <AddContent id={id} className={className} key={`column-${generateKey()}-${ind}`} parentId={contentElementId} ctType={cl?.contentType} ctData={cl} />
          )
        }

      })
    ) : (
      ctData?.content?.hasOwnProperty("contentList") ? (
        // incase of compound column that contains child columns
        <>
          {/* <pre>{JSON.stringify(ctData, null, 2)}</pre> */}
          <AddRow id={id} className={className} parentId={contentElementId} rlData={ctData} />
        </>
      ) : (
        // incase of single column that directly contains leaf-component data
        <PopulateColumnData id={id} className={className} dType={ctData?.contentType} clData={ctData} />
      )
    )
  )
}

/**
 * Name: PopulateColumnData
 * 
 * Description: invoke the HtmlView function to render basic component types on the UI using data from Contentful
 * 
 * @param param0 object containing the following properties:
 *                - dId: unique identifier to be used as key for components displayed in a list
 *                - dIndex: unique identifier for rendering contents within HtmlView (passed in viewIndex prop while calling HtmlView)
 *                - data: an object containing the content to be displayed in the UI
 *                - dtType: string representing the content type
 * @returns HtmlView component which in turn renders data on the UI
 */
export const PopulateColumnData = ({ id = "", className = "", dIndex = 0, clData }: { id?: string | undefined, className?: string, dId?: string, dType: string | undefined, dIndex?: number, clData: T_RLData }) => {
  return (
    <HtmlView id={id} className={className} cType={clData?.contentType} viewIndex={dIndex} data={clData} />
  )
}


const VirtualComponent = ({contentData, struct, viewIndex}: {contentData: T_RLData, struct?: T_ContentItem, viewIndex: number}) => {
  const vData: T_RLData = contentData; //useContentfulLiveUpdates(contentData);
  // const inspectorModeProps = useContentfulInspectorMode();

  return (
    <div 
      // {...inspectorModeProps({fieldId: vData?.contentType || "baseplateMedia", entryId: vData?.id})}
      >
      {Object.entries(vData?.content ?? {})?.map(([key, value]: [string, unknown], index: number) => {
        return (
        () => {
          if (key.toLowerCase().includes("date")) {
            return <StyledDate key={"render-" + (new Date()).getTime() + "-" + viewIndex + index}>{(new Date(value as string)).toDateString()}</StyledDate>
          } else if (typeof (value) === "string") {
            const contentObj: T_ContentItem = {
              "id": (new Date()).getTime().toString(),
              "internalName": "",
              "title": "",
              "contentType": key,
              "type": "Entry",
              "content": {
                "text": value,
                "widthUnit": "%",
                "maxWidth": 100,
                "fontUnit": "px",
                "textAlign": "left"
              },
              "locale": "en-US"
            }

            // return (
            //   <>
            //     {key}<br />
            //     <pre>{JSON.stringify(value, null, 2)}</pre>
            //     <HtmlView key={"render-" + contentObj?.contentType + "-" + viewIndex + index} cType={contentObj?.contentType} viewIndex={index} data={contentObj} struct={struct} />
            //   </>
            return (
              <HtmlView key={"render-" + contentObj?.contentType + "-" + viewIndex + '--' + generateKey()} cType={contentObj?.contentType} viewIndex={index} data={contentObj} struct={struct} />
            )
          } else {
            return (
              <div key={"render-" + (value as T_ContentItem)?.contentType + "-" + viewIndex + index}>
                <HtmlView cType={(value as T_ContentItem)?.contentType} viewIndex={index} data={(value as T_ContentItem)?.nodeType === "document" ? { content: { richText: value } } : (value as T_ContentItem)} struct={struct} />
              </div>
            )
          }
        }

        )();
      })}
    </div>
  )
}


/**
 * Name: HtmlView
 * 
 * Description: a functional component that renders html content based on the content type passed (using switch case). It populates the UI using the Contentful data passed to it as props
 * 
 * @param param0 object containing the following properties:
 *                - cType: content type of the component that needs to be rendered
 *                - viewIndex: unique identifier used as a key in the component to be rendered on UI
 *                - data: an object containing the content that needs to be displayed as the component whose content type is passed as props
 *                - struct: structure of the content to be rendered, as received from Contentful
 * 
 * @example Parameter breakup
 * ```
 *    param0: {
 *      cType: "",       //content type of the component that needs to be rendered
 *      viewIndex: "",   //unique identifier used as a key in the component to be rendered on UI
 *      data: "",        //an object containing the content that needs to be displayed/ rendered
 *      struct: "",      //structure of the CT to be rendered, as received from Contentful
 *     }
 * ```
 * 
 * @returns a React component that is displayed on the UI by populating with data inside props
 */
export const HtmlView = ({ id, className = "", numberOfColumns = 12, cType = "", viewIndex = 0, data, struct, locale }: T_ViewProps) => {
  const localizedHomeHref = locale && locale !== getDefaultLocale() ? `/${locale}` : "/";


  return (
    <>
      {/* {JSON.stringify(cType)} */}
      {
        (() => {
          if (["contentTitle", "cssClass", "customStyles"].includes(cType)) return;
          switch (cType) {
            case "baseplateInputField":
            case "inputField":
              return <InputField id={(data?.content?.id || data?.id || id) as string} className={classNames(`input-${cType}`, className)} typeid={cType} data={data} name={(data?.content?.name || data?.name || "text-input") as string} type="text" />
            case "baseplateSelect":
              return <Select id={(data?.content?.id || data?.id || id) as string} className={className} typeid={cType} data={data} />
            case "baseplateSection":
            case "section":
            case "blocks":
              return (
                <Section
                  typeid={cType}
                  id={(data?.content?.id || data?.id || id) as string}
                  data={data as T_RLData}
                  className={classNames(data?.title || '', data?.content?.cssClass || '')}
                  customStyles={
                    {
                      /**
                       * Adding css properties from Contentful editor side (we provided inputs on the editor'e section to add these css properties.)
                       */
                      ...(data?.content?.customStyles || {}),
                      ...(data?.content?.paddingTop || data?.content?.paddingTop === 0 ? { paddingTop: data?.content?.paddingTop + (data?.content?.paddingUnit || 'px') } : {}),
                      ...(data?.content?.paddingRight || data?.content?.paddingRight === 0 ? { paddingRight: data?.content?.paddingRight + (data?.content?.paddingUnit || 'px') } : {}),
                      ...(data?.content?.paddingBottom || data?.content?.paddingBottom === 0 ? { paddingBottom: data?.content?.paddingBottom + (data?.content?.paddingUnit || 'px') } : {}),
                      ...(data?.content?.paddingLeft || data?.content?.paddingLeft === 0 ? { paddingLeft: data?.content?.paddingLeft + (data?.content?.paddingUnit || 'px') } : {}),
                      ...(data?.content?.backgroundColor ? { backgroundColor: data?.content?.backgroundColor } : {})
                    }
                  }
                >
                  {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
                  {data?.content?.columns && (<AddRow parentId={`sect-${data?.id}-row`} rlData={data?.content} />)}
                </Section>
              )
            case "baseplateColumn":
            case "column":
              return <>
                {/* <pre>{JSON.stringify(data?.title, null, 2)}</pre> */}
                {data?.content &&
                  (() => {
                    switch (data?.content?.useAs) {
                      case "Card":
                        return (
                          <Card
                            id={id || "crd"}
                            className={classNames(data?.content?.columnTitle || '', data?.title || '', data?.content?.cssClass || '')}
                            customStyles={
                              {
                                /**
                                 * Adding css properties from Contentful editor side (we provided inputs on the editor'e section to add these css properties.)
                                 */

                                ...data?.content?.customStyles,
                                ...(data?.content?.paddingTop || data?.content?.paddingTop === 0 ? { paddingTop: data?.content?.paddingTop + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.paddingRight || data?.content?.paddingRight === 0 ? { paddingRight: data?.content?.paddingRight + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.paddingBottom || data?.content?.paddingBottom === 0 ? { paddingBottom: data?.content?.paddingBottom + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.paddingLeft || data?.content?.paddingLeft === 0 ? { paddingLeft: data?.content?.paddingLeft + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.backgroundColor ? { backgroundColor: data?.content?.backgroundColor } : {}),
                                ...(data?.content?.backgroundImage ? { backgroundImage: `url(${data?.content?.backgroundImage?.content?.file?.url})` } : {}),
                                lg: {
                                  ...(data?.content?.minWidthDesktop || data?.content?.minWidthDesktop === 0 ? { minWidth: data?.content?.minWidthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.maxWidthDesktop || data?.content?.maxWidthDesktop === 0 ? { maxWidth: data?.content?.maxWidthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.widthDesktop || data?.content?.widthDesktop === 0 ? { width: data?.content?.widthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.minHeightDesktop || data?.content?.minHeightDesktop === 0 ? { minHeight: data?.content?.minHeightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.maxHeightDesktop || data?.content?.maxHeightDesktop === 0 ? { maxHeight: data?.content?.maxHeightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.heightDesktop || data?.content?.heightDesktop === 0 ? { height: data?.content?.heightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                },
                                md: {
                                  ...(data?.content?.minWidthTab || data?.content?.minWidthTab === 0 ? { minWidth: data?.content?.minWidthTab + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.maxWidthTab || data?.content?.maxWidthTab === 0 ? { maxWidth: data?.content?.maxWidthTab + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.widthTab || data?.content?.widthTab === 0 ? { width: data?.content?.widthTab + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.minHeightTab || data?.content?.minHeightTab === 0 ? { minHeight: data?.content?.minHeightTab + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.maxHeightTab || data?.content?.maxHeightTab === 0 ? { maxHeight: data?.content?.maxHeightTab + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.heightTab || data?.content?.heightTab === 0 ? { height: data?.content?.heightTab + (data?.content?.heightUnit || 'px') } : {}),
                                },
                                xs: {
                                  ...(data?.content?.minWidthMobile || data?.content?.minWidthMobile === 0 ? { minWidth: data?.content?.minWidthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.maxWidthMobile || data?.content?.maxWidthMobile === 0 ? { maxWidth: data?.content?.maxWidthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.widthMobile || data?.content?.widthMobile === 0 ? { width: data?.content?.widthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.minHeightMobile || data?.content?.minHeightMobile === 0 ? { minHeight: data?.content?.minHeightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.maxHeightMobile || data?.content?.maxHeightMobile === 0 ? { maxHeight: data?.content?.maxHeightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.heightMobile || data?.content?.heightMobile === 0 ? { height: data?.content?.heightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                }
                              }
                            }
                          >
                            <HtmlView id={"card-" + data?.id + "-" + viewIndex} cType={data?.content?.contentType} data={data} struct={struct} />
                          </Card>
                        )
                      case "Box":
                        return (
                          <Box
                            id={id || "bx"}
                            className={classNames(data?.content?.columnTitle || '', data?.title || '', data?.content?.cssClass || '')}
                            customStyles={
                              {
                                /**
                                 * Adding css properties from Contentful editor side (we provided inputs on the editor'e section to add these css properties.)
                                 */

                                ...data?.content?.customStyles,
                                ...(data?.content?.paddingTop || data?.content?.paddingTop === 0 ? { paddingTop: data?.content?.paddingTop + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.paddingRight || data?.content?.paddingRight === 0 ? { paddingRight: data?.content?.paddingRight + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.paddingBottom || data?.content?.paddingBottom === 0 ? { paddingBottom: data?.content?.paddingBottom + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.paddingLeft || data?.content?.paddingLeft === 0 ? { paddingLeft: data?.content?.paddingLeft + (data?.content?.paddingUnit || 'px') } : {}),
                                ...(data?.content?.backgroundColor ? { backgroundColor: data?.content?.backgroundColor } : {}),
                                ...(data?.content?.backgroundImage ? { backgroundImage: `url(${data?.content?.backgroundImage?.content?.file?.url})` } : {}),
                                ...(data?.content?.backgroundImage ? { backgroundRepeat: data?.content?.backgroundRepeat || "no-repeat" } : {}),
                                ...(data?.content?.backgroundImage ? { backgroundPosition: data?.content?.backgroundPosition || "center" } : {}),
                                ...(data?.content?.backgroundImage ? { backgroundSize: data?.content?.backgroundSize || "contain" } : {}),
                                lg: {
                                  ...(data?.content?.minWidthDesktop || data?.content?.minWidthDesktop === 0 ? { minWidth: data?.content?.minWidthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.maxWidthDesktop || data?.content?.maxWidthDesktop === 0 ? { maxWidth: data?.content?.maxWidthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.widthDesktop || data?.content?.widthDesktop === 0 ? { width: data?.content?.widthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.minHeightDesktop || data?.content?.minHeightDesktop === 0 ? { minHeight: data?.content?.minHeightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.maxHeightDesktop || data?.content?.maxHeightDesktop === 0 ? { maxHeight: data?.content?.maxHeightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.heightDesktop || data?.content?.heightDesktop === 0 ? { height: data?.content?.heightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                },
                                md: {
                                  ...(data?.content?.minWidthTab || data?.content?.minWidthTab === 0 ? { minWidth: data?.content?.minWidthTab + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.maxWidthTab || data?.content?.maxWidthTab === 0 ? { maxWidth: data?.content?.maxWidthTab + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.widthTab || data?.content?.widthTab === 0 ? { width: data?.content?.widthTab + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.minHeightTab || data?.content?.minHeightTab === 0 ? { minHeight: data?.content?.minHeightTab + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.maxHeightTab || data?.content?.maxHeightTab === 0 ? { maxHeight: data?.content?.maxHeightTab + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.heightTab || data?.content?.heightTab === 0 ? { height: data?.content?.heightTab + (data?.content?.heightUnit || 'px') } : {}),
                                },
                                xs: {
                                  ...(data?.content?.minWidthMobile || data?.content?.minWidthMobile === 0 ? { minWidth: data?.content?.minWidthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.maxWidthMobile || data?.content?.maxWidthMobile === 0 ? { maxWidth: data?.content?.maxWidthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.widthMobile || data?.content?.widthMobile === 0 ? { width: data?.content?.widthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                  ...(data?.content?.minHeightMobile || data?.content?.minHeightMobile === 0 ? { minHeight: data?.content?.minHeightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.maxHeightMobile || data?.content?.maxHeightMobile === 0 ? { maxHeight: data?.content?.maxHeightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                  ...(data?.content?.heightMobile || data?.content?.heightMobile === 0 ? { height: data?.content?.heightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                }
                              }
                            }
                          >
                            {/* <>My Box</> */}
                            {/* <pre>{JSON.stringify(data.content, null, 2)}</pre> */}
                            {data?.content?.contentList?.map((el: T_ContentItem) => {
                              return (() => {
                                let contentElementId = `${id && id != "clm" ? id + "-" : ""}clm`
                                contentElementId += `-${el?.id}-${generateKey()}`
                                if (el?.content?.hasOwnProperty("columns")) {
                                  return (
                                    <Row key={`column-arr-cl-${el?.id}-${generateKey()}`} id={(data?.content?.id || data?.id || id || contentElementId) as string} className={classNames(el?.title, className)} customStyles={el?.content?.customStyles}>
                                      <AddColumn id={id} clContent={el?.content} parentId={contentElementId} />
                                    </Row>
                                  )
                                } else {
                                  return (
                                    <AddContent id={id} className={className} key={`cl-element-${el?.id}-${generateKey()}`} parentId={contentElementId} ctType={el?.contentType} ctData={el} />
                                  )
                                }
                              })()
                            })}
                            {/* <HtmlView key={"box-" + data?.id + "-" + viewIndex} cType={data?.content?.contentType} data={data} struct={struct} /> */}
                          </Box>
                        )
                      default:
                        return (
                          <>
                            {/* <Row id={`row-${data?.id}`} className={data?.title || ''} customStyles={data?.content?.customStyles || {}}> */}
                            {/* <AddColumn parentId={`row-${data?.id}`} clContent={data?.content} /> */}
                            {/* </Row> */}

                            <Column
                              id={(data?.content?.id || data?.id || id) as string || "clm"}
                              numberOfColumns={numberOfColumns}
                              className={classNames(data?.content?.columnTitle || '', data?.title || '', data?.content?.cssClass || '')}
                              customStyles={
                                {
                                  /**
                                   * Adding css properties from Contentful editor side (we provided inputs on the editor'e section to add these css properties.)
                                   */
                                  ...data?.content?.customStyles,
                                  ...(data?.content?.paddingTop || data?.content?.paddingTop === 0 ? { paddingTop: data?.content?.paddingTop + (data?.content?.paddingUnit || 'px') } : {}),
                                  ...(data?.content?.paddingRight || data?.content?.paddingRight === 0 ? { paddingRight: data?.content?.paddingRight + (data?.content?.paddingUnit || 'px') } : {}),
                                  ...(data?.content?.paddingBottom || data?.content?.paddingBottom === 0 ? { paddingBottom: data?.content?.paddingBottom + (data?.content?.paddingUnit || 'px') } : {}),
                                  ...(data?.content?.paddingLeft || data?.content?.paddingLeft === 0 ? { paddingLeft: data?.content?.paddingLeft + (data?.content?.paddingUnit || 'px') } : {}),
                                  ...(data?.content?.backgroundColor ? { backgroundColor: data?.content?.backgroundColor } : {}),
                                  ...(data?.content?.backgroundImage ? { backgroundImage: `url(${data?.content?.backgroundImage?.content?.file?.url})` } : {}),
                                  // [`@media (minWidth:${theme?.screens?.xs || 0})`]: { // for mobile screens
                                  xs: {
                                    ...(data?.content?.minWidthMobile || data?.content?.minWidthMobile === 0 ? { minWidth: data?.content?.minWidthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.maxWidthMobile || data?.content?.maxWidthMobile === 0 ? { maxWidth: data?.content?.maxWidthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.widthMobile || data?.content?.widthMobile === 0 ? { width: data?.content?.widthMobile + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.minHeightMobile || data?.content?.minHeightMobile === 0 ? { minHeight: data?.content?.minHeightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                    ...(data?.content?.maxHeightMobile || data?.content?.maxHeightMobile === 0 ? { maxHeight: data?.content?.maxHeightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                    ...(data?.content?.heightMobile || data?.content?.heightMobile === 0 ? { height: data?.content?.heightMobile + (data?.content?.heightUnit || 'px') } : {}),
                                  },
                                  // [`@media (minWidth:${theme?.screens?.md || 768})`]: { // for tab screens
                                  md: {
                                    ...(data?.content?.minWidthTab || data?.content?.minWidthTab === 0 ? { minWidth: data?.content?.minWidthTab + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.maxWidthTab || data?.content?.maxWidthTab === 0 ? { maxWidth: data?.content?.maxWidthTab + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.widthTab || data?.content?.widthTab === 0 ? { width: data?.content?.widthTab + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.minHeightTab || data?.content?.minHeightTab === 0 ? { minHeight: data?.content?.minHeightTab + (data?.content?.heightUnit || 'px') } : {}),
                                    ...(data?.content?.maxHeightTab || data?.content?.maxHeightTab === 0 ? { maxHeight: data?.content?.maxHeightTab + (data?.content?.heightUnit || 'px') } : {}),
                                    ...(data?.content?.heightTab || data?.content?.heightTab === 0 ? { height: data?.content?.heightTab + (data?.content?.heightUnit || 'px') } : {}),
                                  },
                                  // [`@media (minWidth:${theme?.screens?.lg || 1024})`]: { // for desktop screens
                                  lg: {
                                    ...(data?.content?.minWidthDesktop || data?.content?.minWidthDesktop === 0 ? { minWidth: data?.content?.minWidthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.maxWidthDesktop || data?.content?.maxWidthDesktop === 0 ? { maxWidth: data?.content?.maxWidthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.widthDesktop || data?.content?.widthDesktop === 0 ? { width: data?.content?.widthDesktop + (data?.content?.widthUnit || 'px') } : {}),
                                    ...(data?.content?.minHeightDesktop || data?.content?.minHeightDesktop === 0 ? { minHeight: data?.content?.minHeightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                    ...(data?.content?.maxHeightDesktop || data?.content?.maxHeightDesktop === 0 ? { maxHeight: data?.content?.maxHeightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                    ...(data?.content?.heightDesktop || data?.content?.heightDesktop === 0 ? { height: data?.content?.heightDesktop + (data?.content?.heightUnit || 'px') } : {}),
                                  }
                                }
                              }
                            >
                              {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
                              {/* {data?.contentList?.map((el: T_KeyValue, ind: number) => {
                                (()=>{
                                  let columnElementId = `${id && id != "clm" ? id + "-" : ""}clm`
                                  columnElementId += `-${ind}`
                                  if (el?.content?.hasOwnProperty("columns")) {
                                    return (
                                      <Row key={`column-arr-cl-${el?.id}`} id={columnElementId} className={el?.title} customStyles={el?.content?.customStyles}>
                                        <AddColumn clContent={el?.content} parentId={columnElementId} />
                                      </Row>
                                    )
                                  } else {
                                    return (
                                      <AddContent key={`cl-element-${el?.id}`} parentId={columnElementId} ctType={el?.contentType} ctData={el} />
                                    )
                                  }
                                })()
                              })} */}

                              {/* <>My Column</> */}
                              {data?.content?.contentList && (<AddContent ctType={data?.contentType} parentId={id || "clm"} ctData={data} />)}
                            </Column>
                          </>
                        )
                    }
                  })()
                }
              </>
            case "baseplateConfig":
              return (
                <>
                  {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
                  {(() => {
                    switch (data?.content?.configuration) {
                      case "App Integration":
                        return (() => {
                          switch (data?.content?.appIntegrationType) {
                            case "Iframe": // Logic for iframe integration will come here
                              return (
                                <IframeWrapper src={data?.content?.src} height={(data?.content?.iframeHeight) as string} width={(data?.content?.iframeWidth) as string} />
                              )
                            case "Embed": // Logic for script integration will come here
                              return (
                                <>
                                  <DomParser dHtml={documentToHtmlString(data?.content?.embedScript as Document)} />
                                </>
                              )
                            default: // Logic for other integration will come here
                              return (<></>)
                          }
                        })()
                      // case "App Config":
                      //   if(data?.content?.appConfig?.expEnabled || data?.content?.appConfig?.expSlug) {
                      //     return (
                      //       () => {
                      //         'use client'
                      //         if(document.querySelector(".Experience-impl")) {
                      //           return <></>
                      //         } 
                      //         // else {
                      //         //   return <Experience preview={false} slug={data?.content?.appConfig?.expSlug} locale={locale} />
                      //         // }
                      //       }
                      //     )()
                      //   } else {
                      //     return (<></>);
                      //   }
                      default:
                        return (<></>)
                    }
                  })()}
                </>
              )
            case "baseplateOverlay":
            case "overlay":
              return (
                <Overlay
                  id={data?.content?.id || data?.id || id}
                  typeid={cType}
                  className={classNames(data?.title || '', data?.content?.cssClass || "")}
                  customStyles={{
                    /**
                     * Adding css properties from Contentful editor side (we provided inputs on the editor'e section to add these css properties.)
                     */
                    ...(data?.content?.paddingTop || data?.content?.paddingTop === 0 ? { paddingTop: data?.content?.paddingTop + (data?.content?.paddingUnit || 'px') } : {}),
                    ...(data?.content?.paddingRight || data?.content?.paddingRight === 0 ? { paddingRight: data?.content?.paddingRight + (data?.content?.paddingUnit || 'px') } : {}),
                    ...(data?.content?.paddingBottom || data?.content?.paddingBottom === 0 ? { paddingBottom: data?.content?.paddingBottom + (data?.content?.paddingUnit || 'px') } : {}),
                    ...(data?.content?.paddingLeft || data?.content?.paddingLeft === 0 ? { paddingLeft: data?.content?.paddingLeft + (data?.content?.paddingUnit || 'px') } : {}),
                  }}
                  backgroundColor={data?.content?.backgroundColor}
                  overlayOpacity={data?.content?.overlayOpacity}
                >
                  {data?.content?.overlayContent ? (
                    data?.content?.overlayContent?.map((oc: T_ContentItem) => {
                      return <AddContent parentId={`ovrl-${data?.id}`} key={`overlay-content-${oc?.id}-${generateKey()}`} ctType={oc?.contentType} ctData={oc} />
                    })
                  ) : (
                    // <>My Overlay</>
                    <AddContent parentId={`ovrl-${data?.id}`} ctType={data?.content?.contentType} ctData={data?.content as T_RLData} />
                  )}
                </Overlay>
              )
            case "baseplateMedia":
            case "baseplateImage":
            case "Asset":
            case "cardImage":
              return <>
                <Media
                  id={id}
                  className={classNames(`media-type-${cType}`, className)}
                  typeid={cType}
                  includes={data?.includes}
                  // style={{maxWidth: "300px"}}
                  data={data}
                />
              </>
            case "baseplateRichText":
            case "baseplateRichtext":
            case "RichText":
            case "richText":
            case "Richtext":
            case "richtext":
            case "blockRichText":
              // case "":
              return <RichTextBox id={`rich-text-${cType}-${viewIndex}`} typeid={cType} data={data} />
            case "baseplateButton":
            case "button":
              return <Button id={data?.content?.id || data?.id || id} cssClass={`button-${cType}`} typeid={cType} data={data} />
            case "baseplateHeading":
            case "cardText":
              return <Typography id={`typography-${cType}-${data?.id}-${viewIndex}`} component={data?.content?.headingLevel || "p"} typeid={cType} data={data} />
            case "baseplateForm":
            case "form":
              return (
                <DynamicFormWrapper id={data?.content?.id || data?.id || id} className={`form-${cType}`} typeid={cType} frmData={data} />
              )
            case "baseplateLink":
            case "blockLink":
            case "link":
              return (
                <CustomLink id={data?.content?.id || data?.id || id} data={data} cssClass={`html-link ${data?.title || ''}`} href={data?.content?.url || data?.content?.slug || localizedHomeHref} locale={locale} target={data?.content?.openInNewTab === true ? "_blank" : ""} />
              )
            case "blockTextAndImageCard":
            case "blockTextAndImage":
            case "libraryMeetingPlace":
            case "meetingPlacePageOccasion":
            case "meetingPlacePageEvent":
            case "librabyMenuGroup":
            case "menuGroup":
            case "blockFooter":
            case "meetingPlacePageTenantShopList":
            case "meetingPlacePageSearchResults":
              return (
                <>
                  {/* {cType}<br/> */}
                  {/* <pre>{JSON.stringify(data?.content, null, 2)}</pre> */}

                  <VirtualComponent contentData={data as T_RLData} struct={struct} viewIndex={viewIndex} />

                  {/* <HtmlView key={"render-" + data?.content?.contentType + "-" + viewIndex} cType={data?.content?.contentType} viewIndex={viewIndex} data={data?.content} struct={struct} /> */}
                </>
              )
            case "Entry":
              return (
                <>
                  {/* {cType}<br/> */}
                  {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}

                  {Object.values(data?.content ?? {})?.map((value: unknown, index: number) => {
                    const ct = value?.hasOwnProperty("nodeType") && (value as T_ContentItem)?.nodeType === "document" ? "RichText" : (value as T_ContentItem)?.contentType;
                    return (
                      <div key={"render-" + (value as T_ContentItem)?.contentType + "-" + viewIndex + (new Date()).getTime()}>
                        {/* {key}<br/> */}
                        {/* <pre>{JSON.stringify(value, null, 2)}</pre> */}
                        <HtmlView key={"render-" + ct + "-" + viewIndex + index} cType={ct} viewIndex={index} data={(value as T_ContentItem)} struct={struct} />
                      </div>
                    );
                  })}

                  {/* <HtmlView key={"render-" + data?.content?.contentType + "-" + viewIndex} cType={data?.content?.contentType} viewIndex={viewIndex} data={data?.content} struct={struct} /> */}
                </>
              )
            // default:
            //   return <>
            //     {/* {cType}<br/> */}
            //     {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
            //     {/* <RenderHtml rendererIndex={viewIndex} struct={struct} data={data} /> */}
            //     {data?.type === "Entry" && 
            //       <HtmlView key={"render-" + data?.type + "-" + viewIndex} cType={data?.type} viewIndex={viewIndex} data={data} struct={struct} />
            //     }
            default:
              return <>
                <RenderHtml rendererIndex={viewIndex} data={data?.content} />
                {/* <pre>{JSON.stringify(data?.contentType, null, 2)}</pre> */}
                {/* <HtmlView cType={data?.contentType} viewIndex={viewIndex} data={data?.content} struct={struct} /> */}
              </>
          }
        })()
      }
    </>
  )
}

/**
 * Name: RenderHtml
 * 
 * Description: a functional component that uses the data and struct objects pertaining to a certain component to render its contents on the UI. It evokes the HtmlView functional component
 *  
 * @param param0 
 * 
 * 
 * @exapmle Parameter breakup 
 * ```
 *  param0: {
 *    struct: {},          //structure of the content to be rendered, as received from Contentful
 *    data: {},            //an object containing the content to be displayed on the UI
 *    rendereIndex: : 0    //unique identifier used as a key in the HtmlView parent component
 *  }
 * ```
 * 
 * @example I_Props
 * ```
 * interface I_Props {
 *     cType?: string,          // optional
 *     data?: T_KeyValue,              // optional
 *     rendererIndex: number,   
 *     struct?: T_KeyValue,            // optional
}
 * ```
 * 
 * @returns 
 * 
 * @example Using RenderHtml in a page
 * ```
 * <div className="HomePage" data-testid="HomePage">
 *     <RenderHtml data={content?.items} rendererIndex={0} />
 * </div>
 * ``` 
 * 
 * @example data parameter's example value structure: 
 * ```
[
  {
    "id": "1JftnTmvyWUGSyMJujWinO",
    "title": "Landing Page",
    ...
    "content": {
      "pageContent": [
        {
          "id": "4RPzYupOi8b2Fg3ayPzMU9",
          "internalName": "",
          "content": {
            "columns": [
              {
                "id": "19OQ7hdXPDVTzLtneZHeFy",
                "internalName": "",
                ...
                "content": {
                  "contentList": [
                    {
                      "id": "3n7yvZuzZXbFEAmFMWpZlY",
                      "internalName": "",
                      ...
                      "content": {
                        ...
                      },
                      ...
                    }
                    ...
                  ],
                  ...
                }
              }
            ]
          },
          "createdAt": "2024-03-21T11:00:30.205Z",
          "updatedAt": "2024-03-21T11:00:30.205Z",
          "locale": "en-US"
        },
      ]
      ...
    }
  }
]
 * ```
 */
const RenderHtml = ({ struct, data }: I_Props) => {
  if (struct?.hasOwnProperty("items")) {
    /** extracting validations prop within struct object if contained within items prop */
    struct = {
      ...struct,
      ...struct?.items
    }
    delete struct?.items;
  }

  const Content = (data: T_RLData, _pIndex: number = 0) => {
    if (!data?.hasOwnProperty("content")) { data["content"] = deepCopy(data); }
    // console.log("content -------- ", data)
    return (
      Object.values(data?.content || {})?.map((value: unknown, index: number) => {
        return (
          Array.isArray(value) ? (
            value?.map((d: T_ContentItem, index: number) => {
              return (
                <HtmlView key={"render-" + d?.id + "-" + generateKey()} cType={d?.contentType} viewIndex={_pIndex + index} data={d} />
              )
            })
          ) : (
            typeof (value) === "object" && (
              <HtmlView key={"render-" + (value as T_ContentItem)?.id + "-" + generateKey()} cType={(value as T_ContentItem)?.contentType} viewIndex={index} data={(value as T_ContentItem)} />
            )
          )
        )
      })
    )
  }

  return (
    Array.isArray(data) ? (
      data?.map((d: T_ContentItem, index: number) => {
        return (
          Content(d, index)
        )
      })
    ) : (
      typeof (data) === "object" &&
      Content(data)
    )
  );
};

export default RenderHtml;
